import { Box, Paper, Typography, Chip } from '@gapartners/ui';
import { useTranslation } from 'react-i18next';
import type { CallQualityMetricsDTO } from '@/types/qualityMetrics';

interface CallQualityCardProps {
  data: CallQualityMetricsDTO;
}

export function CallQualityCard({ data }: CallQualityCardProps) {
  const { t } = useTranslation();

  const getSentimentColor = (sentiment: string | null) => {
    if (!sentiment) return 'default';
    switch (sentiment) {
      case 'POSITIVE':
        return 'success';
      case 'NEGATIVE':
        return 'error';
      default:
        return 'warning';
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const getDirectionLabel = (direction: string) => {
    switch (direction) {
      case 'improving':
        return t('pages.manager.qualityMetrics.call.components.improving');
      case 'declining':
        return t('pages.manager.qualityMetrics.call.components.declining');
      default:
        return t('pages.manager.qualityMetrics.call.components.stable');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('pages.manager.qualityMetrics.call.components.overallSentiment')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`${t('pages.manager.qualityMetrics.call.components.positive')}: ${data.averageSentiment.positive.toFixed(1)}%`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`${t('pages.manager.qualityMetrics.call.components.neutral')}: ${data.averageSentiment.neutral.toFixed(1)}%`}
              color="warning"
              variant="outlined"
            />
            <Chip
              label={`${t('pages.manager.qualityMetrics.call.components.negative')}: ${data.averageSentiment.negative.toFixed(1)}%`}
              color="error"
              variant="outlined"
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('pages.manager.qualityMetrics.call.components.customerGreeting')}
          </Typography>
          <Chip
            label={
              data.greetingSentiment ||
              t('pages.manager.qualityMetrics.call.components.unknown')
            }
            color={getSentimentColor(data.greetingSentiment)}
            variant="outlined"
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('pages.manager.qualityMetrics.call.components.nonTalkTime')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {formatDuration(data.nonTalkTimeSeconds)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('pages.manager.qualityMetrics.call.components.interruptCount')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {data.interruptCount}{' '}
            {t('pages.manager.qualityMetrics.call.components.times')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2, gridColumn: '1 / -1' }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('pages.manager.qualityMetrics.call.components.sentimentTrend')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={`${t('pages.manager.qualityMetrics.call.components.direction')}: ${getDirectionLabel(data.sentimentTrend.direction)}`}
            color={
              data.sentimentTrend.direction === 'improving'
                ? 'success'
                : data.sentimentTrend.direction === 'declining'
                  ? 'error'
                  : 'default'
            }
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            ({data.sentimentTrend.progression.length}{' '}
            {t('pages.manager.qualityMetrics.call.components.dataPoints')})
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
